// /league/:id/replay/:gameId — watch a played league game again, move by move (bot games are played
// by the server, so this is how you see them).
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getReplay } from '../../leagues/api';
import { runReplay } from '../../games/capture-the-flag/replay';
import type { CtfState } from '../../games/capture-the-flag/types';
import CtfGameScreen from '../games/capture-the-flag/CtfGameScreen';

// Steps a second while playing. A step is one move or action (a unit walking 3 hexes is 3 steps).
const SPEEDS = [{ label: 'Slow', ms: 700 }, { label: 'Normal', ms: 250 }, { label: 'Fast', ms: 80 }, { label: 'Very fast', ms: 25 }];
const btn: React.CSSProperties = { minWidth: 36, minHeight: 32, background: 'var(--sunk)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' };

export default function LeagueReplayPage() {
  const { id, gameId } = useParams<{ id: string; gameId: string }>();
  const [data, setData] = React.useState<{ states: CtfState[]; names: { A: string; B: string }; league: string; blue: string; red: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [at, setAt] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [speed, setSpeed] = React.useState(1);   // index into SPEEDS

  React.useEffect(() => {
    getReplay(id!, gameId!).then(r => {
      const states: CtfState[] = [];
      const run = runReplay(r.replay, s => { states.push(s); });
      states.unshift(run.initial);
      setData({ states, names: run.header.players, league: r.league, blue: r.blue, red: r.red });
      setAt(0);
    }).catch(e => setError((e as Error).message));
  }, [id, gameId]);

  const last = data ? data.states.length - 1 : 0;
  React.useEffect(() => {
    if (!playing) return;
    if (at >= last) { setPlaying(false); return; }
    const t = window.setTimeout(() => setAt(a => Math.min(last, a + 1)), SPEEDS[speed].ms);
    return () => window.clearTimeout(t);
  }, [playing, at, last, speed]);

  const back = <Link to={`/league/${id}`} style={{ color: '#f2c46b', fontWeight: 600 }}>← Back to the league</Link>;
  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#24262e', color: '#ecebf2', padding: 24, fontFamily: 'system-ui, sans-serif', display: 'grid', gap: 12, alignContent: 'start' }}>
        {back}
        <p>{error ?? 'Loading the replay…'}</p>
      </div>
    );
  }

  const step = (n: number) => { setPlaying(false); setAt(Math.max(0, Math.min(last, n))); };
  // Jump by game round: the first step of the next round, or the start of this one (the previous one
  // if already at its start).
  const roundOf = (i: number) => data.states[i].round;
  const nextRound = () => { const r = roundOf(at); const i = data.states.findIndex((x, j) => j > at && x.round > r); step(i < 0 ? last : i); };
  const prevRound = () => {
    let i = at;
    while (i > 0 && roundOf(i - 1) === roundOf(at)) i--;
    if (i === at && i > 0) { const r = roundOf(i - 1); while (i > 0 && roundOf(i - 1) === r) i--; }
    step(i);
  };
  const rounds = data.states[last].round;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#24262e' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px', padding: '8px 16px', color: '#ecebf2', fontFamily: 'system-ui, sans-serif', borderBottom: '1px solid #3b3e49' }}>
        {back}
        <span style={{ opacity: 0.8 }}>Replay · {data.league}</span>
        <strong><span style={{ color: '#8fb8dd' }}>{data.blue}</span> vs <span style={{ color: '#ec9a85' }}>{data.red}</span></strong>
      </div>
      <div style={{ flex: 1 }}>
        <CtfGameScreen
          game={data.states[at]}
          names={data.names}
          controls={() => false}
          isBot={() => false}
          onAction={() => undefined}
          statusNote={` · replay, step ${at} of ${last}`}
          turnExtra={
            <div role="group" aria-label="Replay controls" style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <button type="button" style={btn} aria-label="First step" onClick={() => step(0)}>⏮</button>
                <button type="button" style={btn} aria-label="Previous step" onClick={() => step(at - 1)}>◀</button>
                <button type="button" style={{ ...btn, minWidth: 64 }} onClick={() => { if (at >= last) setAt(0); setPlaying(p => !p); }}>{playing ? 'Pause' : 'Play'}</button>
                <button type="button" style={btn} aria-label="Next step" onClick={() => step(at + 1)}>▶</button>
                <button type="button" style={btn} aria-label="Last step" onClick={() => step(last)}>⏭</button>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" style={btn} onClick={prevRound}>◀ Round</button>
                <span style={{ minWidth: 92, textAlign: 'center' }}>Round {roundOf(at)} of {rounds}</span>
                <button type="button" style={btn} onClick={nextRound}>Round ▶</button>
              </div>
              <input type="range" min={0} max={last} value={at} aria-label="Step" onChange={e => step(Number(e.target.value))} />
              <label className="ctfg-muted ctfg-small">Speed{' '}
                <select value={speed} onChange={e => setSpeed(Number(e.target.value))} style={{ background: 'var(--sunk)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 4 }}>
                  {SPEEDS.map((x, i) => <option key={x.label} value={i}>{x.label}</option>)}
                </select>
              </label>
            </div>
          }
        />
      </div>
    </div>
  );
}
